import CURRENCY_LIST from "constants/CURRENCY_LIST";
import { Plus, Trash2 } from "lucide-react";
import { Campo, RodapeEtapa, Secao, Selecao } from "./campos";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectList from "types/SelectList";
import { AuthContext } from "context/AuthContext";

import { Caixa, getCaixas } from "services/caixas";
import type { Pedido, PedidoItem } from "services/realizarCotacao/realizarCotacao.types";
import type { CaixaEnvio, CaixaEnvioItem, Estagio1Data } from "./Estagio1";

/** Classe das entradas da tabela de itens, que sao muitas e iguais. */
const ENTRADA =
  "h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-300 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary";

/**
 * Em que caixa o item vai: `caixaEntryId` aponta para uma linha de volume.
 *
 * A primeira versão fazia o contrário — cada caixa listava todos os itens com
 * um campo de quantidade —, e aí 5 itens com 3 caixas viravam 15 campos para
 * preencher, sendo que quase todos ficam em zero. Item escolhendo a sua caixa
 * é uma decisão por item, que é como a pessoa embala de verdade.
 *
 * Item que se divide entre caixas (5 camisetas numa, 5 na outra) vira duas
 * linhas. Fica melhor na invoice também, que lista por volume.
 */
type ManualItem = {
  uid: string;
  descricao: string;
  quantidade: string;
  hscode: string;
  preco: string;
  /** Peso de UMA unidade, em kg. Multiplicado pela quantidade. */
  pesoUnit: string;
  caixaEntryId: string;
};

/**
 * Uma linha = um volume físico.
 *
 * Não guarda peso: o peso do volume é a caixa vazia do cadastro mais o que foi
 * embalado dentro dela. Antes era um campo digitado como "peso bruto", e aí o
 * peso do produto nunca entrava na conta — quem não somava na cabeça cotava
 * abaixo do peso real.
 */
type CaixaLinha = {
  entryId: string;
  caixaId: string;
};

type Props = {
  next: () => void;
  data: Estagio1Data;
  setData: React.Dispatch<React.SetStateAction<Estagio1Data>>;
  setShow: (value: React.SetStateAction<boolean>) => void;
  /** Mesmo motivo do Estagio1: o cadastro de caixas muda por fora daqui. */
  aberto?: boolean;
};

const novoUid = () => Math.random().toString(36).slice(2, 10);

const novoItem = (caixaEntryId: string): ManualItem => ({
  uid: novoUid(),
  descricao: "",
  quantidade: "1",
  hscode: "",
  preco: "",
  pesoUnit: "",
  caixaEntryId,
});

const novaCaixa = (): CaixaLinha => ({
  entryId: novoUid(),
  caixaId: "",
});

/** Peso em kg no formato da tela, sem zeros à toa: 1,45 kg. */
const kg = (v: number) => `${String(Number(v.toFixed(3))).replace(".", ",")} kg`;

const toNum = (v: string) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const qtdItem = (it: ManualItem) => Math.max(1, Math.trunc(toNum(it.quantidade)));

const Estagio1NoCsv = ({ setShow, next, data, setData, aberto = true }: Props) => {
  const { t } = useTranslation("dashboard");
  const auth = useContext(AuthContext);

  const [caixas, setCaixas] = useState<CaixaLinha[]>(() => [novaCaixa()]);
  const [pedidoRef, setPedidoRef] = useState("");
  const [itens, setItens] = useState<ManualItem[]>(() => []);
  const [cadastroCaixas, setCadastroCaixas] = useState<Caixa[]>([]);
  const [listaCaixas, setListaCaixas] = useState<SelectList>([]);

  // O primeiro item só pode nascer depois da primeira caixa existir, porque
  // ele guarda em qual delas vai.
  useEffect(() => {
    setItens((prev) =>
      prev.length ? prev : [novoItem(caixas[0]?.entryId || "")],
    );
  }, [caixas]);

  useEffect(() => {
    const load = async () => {
      const caixasResp = await getCaixas();
      setCadastroCaixas(caixasResp);
      setListaCaixas(
        caixasResp.map((c) => ({
          label: `${c.cod_identificacao} (${c.descricao})`,
          value: String(c.id),
        })),
      );
    };
    if (aberto) load();
  }, [aberto]);

  const multiCaixa = caixas.length > 1;

  // Mostra o código da embalagem junto do número do volume. Só "Caixa 1" /
  // "Caixa 2" não diz nada quando as duas são de tamanhos diferentes, que é
  // justamente quando a escolha importa.
  const opcoesCaixa: SelectList = useMemo(
    () =>
      caixas.map((cx, i) => {
        const cadastro = cadastroCaixas.find((c) => c.id === Number(cx.caixaId));
        return {
          label: cadastro
            ? `${i + 1} · ${cadastro.cod_identificacao}`
            : `Caixa ${i + 1}`,
          value: cx.entryId,
        };
      }),
    [caixas, cadastroCaixas],
  );

  const totalPedido = useMemo(
    () => itens.reduce((acc, it) => acc + toNum(it.preco) * toNum(it.quantidade), 0),
    [itens],
  );

  /**
   * Peso de cada volume: a tara da caixa escolhida mais o peso dos itens que
   * foram para dentro dela. É o número que vai para a transportadora.
   */
  const pesoPorVolume = useMemo(() => {
    const mapa = new Map<string, { vazia: number; produtos: number }>();
    caixas.forEach((cx) => {
      const cadastro = cadastroCaixas.find((c) => c.id === Number(cx.caixaId));
      const vazia = toNum(String(cadastro?.peso ?? ""));
      const produtos = itens
        .filter((it) => it.caixaEntryId === cx.entryId)
        .reduce((acc, it) => acc + toNum(it.pesoUnit) * qtdItem(it), 0);
      mapa.set(cx.entryId, { vazia, produtos });
    });
    return mapa;
  }, [cadastroCaixas, caixas, itens]);

  /** Despacho inteiro: todas as caixas vazias mais tudo que vai dentro. */
  const pesoTotalCaixas = useMemo(
    () =>
      Array.from(pesoPorVolume.values()).reduce(
        (acc, p) => acc + p.vazia + p.produtos,
        0,
      ),
    [pesoPorVolume],
  );

  /**
   * Só os produtos. `Estagio1Data.pesoTotal` significa a mesma coisa nos dois
   * fluxos — peso sem embalagem —, senão o Estágio 3 somaria a tara duas vezes
   * ao usar esse campo como fallback.
   */
  const pesoProdutos = useMemo(
    () =>
      Array.from(pesoPorVolume.values()).reduce((acc, p) => acc + p.produtos, 0),
    [pesoPorVolume],
  );

  // Contrato que o Estágio 3 consome para montar os pacotes das duas
  // transportadoras: cada volume com o que foi embalado dentro dele.
  const caixasEnvio = useMemo<CaixaEnvio[]>(
    () =>
      caixas.map((cx) => {
        const peso = pesoPorVolume.get(cx.entryId);
        return {
          entryId: cx.entryId,
          caixaId: cx.caixaId,
          caixa: cadastroCaixas.find((c) => c.id === Number(cx.caixaId)),
          pesoKg: (peso?.vazia ?? 0) + (peso?.produtos ?? 0),
          itens: itens
            .filter((it) => it.caixaEntryId === cx.entryId)
            .map<CaixaEnvioItem>((it) => ({
              descricao: it.descricao || "Item",
              hscode: it.hscode || "",
              qty: qtdItem(it),
              precoUnit: toNum(it.preco),
              pesoUnit: toNum(it.pesoUnit),
            })),
        };
      }),
    [cadastroCaixas, caixas, itens, pesoPorVolume],
  );

  useEffect(() => {
    const clienteId = auth?.user?.clienteId ?? 0;
    const pedidoId = pedidoRef.trim() || "manual";

    const itensPedido: PedidoItem[] = itens.map((it) => ({
      qty: qtdItem(it),
      sku: "",
      preco: toNum(it.preco),
      hscode: it.hscode,
      titulo: it.descricao || "Item",
      __debug: { zipKey: null, cityKey: null },
      // Sai na invoice como peso da linha. Ficava "0" fixo, e como o Estágio 3
      // usa `toNum(it.pesoUnit) || 0.1`, toda linha da invoice da cotação
      // manual saía com 0,1 kg inventado.
      pesoUnit: String(toNum(it.pesoUnit)),
      categoria: "",
      descricao: it.descricao || "Item",
      moedaLinha: data.currencyPrice || "USD",
      valorTotalLinha: toNum(it.preco) * qtdItem(it),
    }));

    const pedido: Pedido = {
      id: 0,
      cliente_id: Number(clienteId) || 0,
      pedido_ref: pedidoId,
      status: false,
      moeda: data.currencyPrice || "USD",
      total: String(totalPedido.toFixed(2)),
      nomeComprador: "",
      emailComprador: null,
      telefoneComprador: null,
      endereco: "",
      cidade: "",
      estado: "",
      CEP: null,
      pais: "",
      itens: itensPedido,
    };

    setData((prev) => ({
      ...prev,
      pedidoId,
      selectedPedido: pedido,
      manualItems: itensPedido,
      hscodes: itens.map((i) => i.hscode || ""),
    }));
  }, [auth?.user?.clienteId, data.currencyPrice, itens, pedidoRef, setData, totalPedido]);

  // Peso total deixa de ser digitado: é a soma dos volumes, que é o que vai
  // para a cotação caixa por caixa.
  useEffect(() => {
    const primeira = caixasEnvio[0];
    setData((prev) => ({
      ...prev,
      caixas: caixasEnvio,
      pesoTotal: pesoProdutos > 0 ? String(pesoProdutos) : "",
      caixaId: primeira?.caixaId || "",
      selectedCaixa: primeira?.caixa,
    }));
  }, [caixasEnvio, pesoProdutos, setData]);

  const itensValidos = useMemo(
    () =>
      itens.length > 0 &&
      itens.every(
        (it) =>
          it.descricao.trim() &&
          toNum(it.quantidade) > 0 &&
          toNum(it.preco) >= 0 &&
          toNum(it.pesoUnit) > 0 &&
          it.hscode.trim(),
      ),
    [itens],
  );

  const caixasValidas = useMemo(
    () => caixas.length > 0 && caixas.every((cx) => cx.caixaId),
    [caixas],
  );

  // Caixa sem nada dentro quase sempre é a pessoa que adicionou o volume e
  // esqueceu de mandar item para lá.
  const caixasVazias = useMemo(
    () =>
      caixas
        .map((cx, i) => ({ numero: i + 1, entryId: cx.entryId }))
        .filter((cx) => !itens.some((it) => it.caixaEntryId === cx.entryId)),
    [caixas, itens],
  );

  const isValid = Boolean(
    data.currencyPrice &&
      data.currencyPay &&
      pedidoRef.trim() &&
      itensValidos &&
      caixasValidas &&
      !caixasVazias.length,
  );

  const addItem = () =>
    setItens((prev) => [...prev, novoItem(caixas[0]?.entryId || "")]);

  const removeItem = (uid: string) =>
    setItens((prev) => (prev.length > 1 ? prev.filter((it) => it.uid !== uid) : prev));

  const updateItem = (uid: string, patch: Partial<ManualItem>) =>
    setItens((prev) => prev.map((it) => (it.uid === uid ? { ...it, ...patch } : it)));

  const addCaixa = () => setCaixas((prev) => [...prev, novaCaixa()]);

  /** Mesma embalagem da ultima linha: envio com varias caixas iguais e o caso
      comum, e repetir a selecao em cada uma e trabalho a toa. */
  const duplicarUltimaCaixa = () =>
    setCaixas((prev) => {
      const ultima = prev[prev.length - 1];
      return [...prev, { ...novaCaixa(), caixaId: ultima?.caixaId ?? "" }];
    });

  const removeCaixa = (entryId: string) => {
    if (caixas.length <= 1) return;

    const sobra = caixas.find((cx) => cx.entryId !== entryId);
    // Item órfão sumiria da cotação sem aviso: vai para a primeira caixa que
    // restou e a pessoa remaneja se quiser.
    setItens((prev) =>
      prev.map((it) =>
        it.caixaEntryId === entryId
          ? { ...it, caixaEntryId: sobra?.entryId || "" }
          : it,
      ),
    );
    setCaixas((prev) => prev.filter((cx) => cx.entryId !== entryId));
  };

  const updateCaixa = (entryId: string, patch: Partial<CaixaLinha>) =>
    setCaixas((prev) =>
      prev.map((cx) => (cx.entryId === entryId ? { ...cx, ...patch } : cx)),
    );

  const colunasItem = multiCaixa
    ? "grid-cols-[28px_1fr_70px_130px_110px_100px_120px_44px]"
    : "grid-cols-[28px_1fr_70px_130px_110px_100px_44px]";

  return (
    <>
      <div className="space-y-5">
        <div className="grid grid-cols-3 gap-4">
          <Campo
            label="ID do envio"
            valor={pedidoRef}
            aoMudar={setPedidoRef}
            placeholder="EX: #49910"
          />
          <Selecao
            label={t("fields.quotation_currency")}
            valor={data.currencyPrice}
            aoMudar={(v) => setData((p) => ({ ...p, currencyPrice: v }))}
            opcoes={CURRENCY_LIST}
          />
          <Selecao
            label={t("fields.payment_currency")}
            valor={data.currencyPay}
            aoMudar={(v) => setData((p) => ({ ...p, currencyPay: v }))}
            opcoes={CURRENCY_LIST}
          />
        </div>

        {/* CAIXAS — antes dos itens: e preciso ter o volume para dizer o que
            vai dentro dele. */}
        <Secao
          titulo="Caixas do envio"
          extra={
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
              {caixas.length} {caixas.length === 1 ? "volume" : "volumes"}
            </span>
          }
        >
          <p className="-mt-1 text-[11px] text-slate-400">
            Uma linha por volume. O peso e calculado: a caixa vazia do cadastro
            mais os itens que vao dentro dela.
          </p>

          <div className="space-y-2">
            {caixas.map((cx, idx) => {
              const peso = pesoPorVolume.get(cx.entryId);
              const vazia = peso?.vazia ?? 0;
              const produtos = peso?.produtos ?? 0;
              const semEmbalagem = !cx.caixaId;

              return (
                <div
                  key={cx.entryId}
                  className={`grid grid-cols-[32px_1fr_170px_32px] items-center gap-3 rounded-xl border px-3 py-2 ${
                    semEmbalagem ? "border-amber-200 bg-amber-50/50" : "border-slate-200"
                  }`}
                >
                  <span
                    className={`grid h-7 w-7 place-items-center rounded-lg text-xs font-semibold ${
                      semEmbalagem
                        ? "bg-amber-100 text-amber-700"
                        : "bg-blue-50 text-brand-primary"
                    }`}
                  >
                    {idx + 1}
                  </span>

                  <select
                    value={cx.caixaId}
                    onChange={(e) => updateCaixa(cx.entryId, { caixaId: e.target.value })}
                    aria-label={`Embalagem da caixa ${idx + 1}`}
                    className="h-9 w-full cursor-pointer rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 outline-none transition-colors focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
                  >
                    <option value="">Escolha a embalagem...</option>
                    {listaCaixas.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>

                  <div className="text-right">
                    {semEmbalagem ? (
                      <span className="text-[11px] text-amber-700">
                        escolha a embalagem
                      </span>
                    ) : (
                      <>
                        <span className="block text-sm font-semibold text-slate-800">
                          {kg(vazia + produtos)}
                        </span>
                        <span className="block text-[10px] text-slate-400">
                          caixa {kg(vazia)} + itens {kg(produtos)}
                        </span>
                      </>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => removeCaixa(cx.entryId)}
                    disabled={caixas.length <= 1}
                    aria-label="Remover caixa"
                    className="grid h-7 w-7 place-items-center rounded-md border border-slate-200 text-red-500 transition-colors hover:border-red-200 hover:bg-red-50 disabled:opacity-25 disabled:hover:border-slate-200 disabled:hover:bg-transparent"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={addCaixa}
              className="flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline"
            >
              <Plus className="h-3 w-3" />
              Adicionar caixa
            </button>
            <button
              type="button"
              onClick={duplicarUltimaCaixa}
              className="text-xs text-slate-500 hover:text-slate-700 hover:underline"
            >
              Duplicar a ultima
            </button>
            <span className="ml-auto text-xs text-slate-500">
              Peso total{" "}
              <strong className="font-semibold text-slate-800">
                {kg(pesoTotalCaixas)}
              </strong>
            </span>
          </div>
        </Secao>

        {/* ITENS */}
        <Secao
          titulo="Itens do envio"
          extra={
            <span className="text-[11px] text-slate-400">
              A descricao sai na invoice, em ingles
            </span>
          }
        >
          {multiCaixa && (
            <p className="-mt-1 text-[11px] text-slate-400">
              Escolha em qual caixa cada item vai. Item que se divide entre
              caixas: adicione duas linhas.
            </p>
          )}

          <div className="overflow-hidden rounded-xl border border-slate-200">
            <div
              className={`grid ${colunasItem} items-center gap-2 bg-slate-50 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500`}
            >
              <span>#</span>
              <span>Descricao</span>
              <span className="text-center">Qtd</span>
              <span>HS Code / NCM</span>
              <span>Valor unit.</span>
              <span>Peso unit.</span>
              {multiCaixa ? <span>Caixa</span> : null}
              <span />
            </div>

            {itens.map((it, idx) => (
              <div
                key={it.uid}
                className={`grid ${colunasItem} items-center gap-2 border-t border-slate-100 px-3 py-2`}
              >
                <span className="text-xs text-slate-400">{idx + 1}</span>

                <input
                  value={it.descricao}
                  onChange={(e) => updateItem(it.uid, { descricao: e.target.value })}
                  placeholder="Women's knit pants"
                  aria-label="Descricao do item"
                  className={ENTRADA}
                />

                <input
                  value={it.quantidade}
                  onChange={(e) => updateItem(it.uid, { quantidade: e.target.value })}
                  inputMode="numeric"
                  aria-label="Quantidade"
                  className={`${ENTRADA} text-center`}
                />

                <input
                  value={it.hscode}
                  onChange={(e) =>
                    updateItem(it.uid, {
                      hscode: e.target.value.replace(/[^\d.]/g, "").slice(0, 10),
                    })
                  }
                  placeholder="6104.63.00"
                  aria-label="HS Code"
                  className={ENTRADA}
                />

                <input
                  value={it.preco}
                  onChange={(e) =>
                    updateItem(it.uid, {
                      preco: e.target.value.replace(/[^\d.]/g, "").slice(0, 10),
                    })
                  }
                  placeholder="30"
                  aria-label="Valor unitario"
                  className={ENTRADA}
                />

                <input
                  value={it.pesoUnit}
                  onChange={(e) =>
                    updateItem(it.uid, {
                      pesoUnit: e.target.value.replace(/[^\d.,]/g, "").slice(0, 8),
                    })
                  }
                  placeholder="0,4"
                  aria-label="Peso unitario em kg"
                  className={ENTRADA}
                />

                {multiCaixa ? (
                  <select
                    value={it.caixaEntryId}
                    onChange={(e) => updateItem(it.uid, { caixaEntryId: e.target.value })}
                    aria-label="Caixa do item"
                    className={`${ENTRADA} cursor-pointer`}
                  >
                    {opcoesCaixa.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : null}

                <button
                  type="button"
                  onClick={() => removeItem(it.uid)}
                  disabled={itens.length <= 1}
                  aria-label="Remover item"
                  className="grid h-7 w-7 place-items-center rounded-md border border-slate-200 text-red-500 transition-colors hover:border-red-200 hover:bg-red-50 disabled:opacity-25 disabled:hover:border-slate-200 disabled:hover:bg-transparent"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={addItem}
              className="flex w-full items-center justify-center gap-1 border-t border-dashed border-slate-200 py-2.5 text-xs font-medium text-brand-primary transition-colors hover:bg-blue-50/40"
            >
              <Plus className="h-3 w-3" />
              Adicionar item
            </button>
          </div>

          {caixasVazias.length ? (
            <p className="text-xs text-amber-700">
              {caixasVazias.length === 1
                ? `A caixa ${caixasVazias[0].numero} esta sem nenhum item.`
                : `As caixas ${caixasVazias
                    .map((c) => c.numero)
                    .join(", ")} estao sem itens.`}
            </p>
          ) : null}
        </Secao>
      </div>

      <RodapeEtapa
        onCancelar={() => setShow(false)}
        onAvancar={next}
        avancarLiberado={isValid}
        rotuloAvancar="Continuar para dados"
        dica={
          isValid
            ? `${caixas.length} ${caixas.length === 1 ? "volume" : "volumes"} - ${kg(pesoTotalCaixas)}`
            : !caixasValidas
              ? "Toda caixa precisa de embalagem"
              : caixasVazias.length
                ? "Ha caixa sem item"
                : "Preencha descricao, HS Code e peso de todo item"
        }
      />
    </>
  );
};

export default Estagio1NoCsv;
